import { HttpClient, HttpResponse } from '@angular/common/http';
import { Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbInputDatepicker, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { Page } from 'src/app/classes/Page';
import { formatRoles, User } from 'src/app/classes/User';
import { UserChildrenInfoComponent } from 'src/app/components/user/user-children-info/user-children-info.component';
import { UserSubscriptionsInfoComponent } from 'src/app/components/user/user-subscriptions-info/user-subscriptions-info.component';
import { Roles } from 'src/app/enums/Roles';
import { CustomerViewModes, ViewModes } from 'src/app/enums/UsersViewModes';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { UserService } from 'src/app/services/user/user.service';
import { environment } from 'src/environments/environment';
import { ConfirmDialogComponent } from '../../dialog/confirm-dialog/confirm-dialog.component';
import { UserEditComponent } from '../user-edit/user-edit.component';
import { Subscription } from 'src/app/classes/Subscription';
import {
  SubscriptionService,
} from 'src/app/services/subscription.service';
import { UserAddSubscriptionComponent } from 'src/app/components/user/user-add-subscription/user-add-subscription.component';
import { UserAddSubscriptionModalResult } from 'src/app/components/user/user-add-subscription/user-add-subscription.component';
import { Payment } from 'src/app/classes/PaimentReport';
import { PaymentReportFilters } from 'src/app/enums/PaymentReportFilters';
import { PaymentMethods } from 'src/app/enums/PaymentMethods';
import { SortableHeader, SortEvent } from 'src/app/components/user/user-list/Sortable';
import { Child } from 'src/app/classes/Child';

@Component({
  selector: 'app-user-list',
  styleUrls: ['./user-list.component.scss'],
  templateUrl: './user-list.component.html',
})
export class UserListComponent implements OnInit {

  public users: User[];
  public subscriptions: Subscription[] = [];
  public errorMessage: string;
  private currentDialog = null;
  public reportURL: SafeUrl;
  public isAdmin = false;
  public isSuperAdmin = false;
  public pageNumber = 1;
  public totalElements = 0;
  public pageSizes = [10, 20, 50];
  public pageSize = this.pageSizes[0];

  public reportPageNumber = 1;
  public reportPageSize = this.pageSizes[0];
  public reportTotalElements = 0;
  public payments: Payment[];

  public roles = Roles;
  public mode = ViewModes.USERS;
  public modes = ViewModes;
  private userRoles = [];
  public withSubscriptions = false;
  public viewModes = CustomerViewModes;
  public viewMode = CustomerViewModes.LIST;
  private filters: PaymentReportFilters = {
    isPaid: true
  };
  public paymentMethods = Object.values(PaymentMethods);
  public dateFilterValue = null;
  public column: string;
  public direction: string;
  public allUsers;
  public fromDate: NgbDate | null = null;
  public toDate: NgbDate | null = null;
  public hoveredDate: NgbDate | null = null;

  public readonly formatRoles = formatRoles;
  @ViewChild('datepicker', { static: false, read: NgbInputDatepicker }) public datepicker: NgbInputDatepicker;
  @ViewChildren(SortableHeader) headers: QueryList<SortableHeader>;

  constructor(
    private userService: UserService,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService,
    private route: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    private calendar: NgbCalendar,
    private formatter: NgbDateParserFormatter,
  ) {
  }

  ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.userRoles = data.roles;
      this.mode = data.mode;
      const user = this.localStorageService.getUser();
      this.isAdmin = user.roles.includes('ROLE_ADMIN');
      this.isSuperAdmin = user.roles.includes('ROLE_SUPER_ADMIN');
      this.refreshList();
    });
  }

  refreshList(): void {
    this.userService
      .getAllUsers(
        this.pageNumber - 1,
        this.pageSize,
        this.userRoles,
        this.mode !== ViewModes.USERS ? !this.withSubscriptions : false
      )
      .subscribe({
        next: (response: Page<User>) => {
          this.users = response.content;
          this.totalElements = response.totalElements;
        },
        error: (error) => (this.errorMessage = error.error.error),
      });
    this.userService
      .getAllUsers(
        this.pageNumber - 1,
        this.pageSize,
        Object.keys(this.roles), true
      )
      .subscribe({
        next: (response: Page<User>) => {
          this.allUsers = response.content.map(user => {
            return {...user, label: `${user.firstName} ${user.lastName}`};
          });
          this.totalElements = response.totalElements;
        },
        error: (error) => (this.errorMessage = error.error.error),
      });
    this.subscriptionService.getAll().subscribe({
      next: (subscriptions) => {
        this.subscriptions = subscriptions;
      },
      error: (error) => this.errorMessage = error.error.error
    });
  }

  refreshHistory(): void {
    if (this.isAdmin || this.isSuperAdmin) {
      this.userService.getPaymentsHistory(this.reportPageNumber - 1, this.reportPageSize, this.filters, {
        column: this.column,
        direction: this.direction
      }).subscribe({
        next: (response: Page<Payment>) => {
          this.payments = response.content;
          this.reportTotalElements = response.totalElements;
        },
        error: (error) => this.errorMessage = error.error.error
      });
    }
  }

  openEditModal(user: User): void {
    this.currentDialog = this.modalService.open(UserEditComponent, {
      scrollable: true,
      windowClass: 'modal-user-edit',
      size: 'lg',
      centered: true,
    });

    this.currentDialog.componentInstance.user = user;

    this.currentDialog.result.then(
      (result) => {
        if (result instanceof FormGroup) {
          const editedUser = new User(result.value);

          this.userService.addOrEdit(editedUser).subscribe({
            next: () => this.refreshList(),
            error: (error) => (this.errorMessage = error.error.error),
          });
        }
      },
      (error) => {
        console.log('Dialog close error: ' + error);
      }
    );
  }

  openSubscriptionsInfoModal(user: User): void {
    this.currentDialog = this.modalService.open(
      UserSubscriptionsInfoComponent,
      {
        scrollable: true,
        windowClass: 'modal-subscriptions-info',
        size: 'lg',
        centered: true,
      }
    );

    this.currentDialog.componentInstance.user = user;

    this.currentDialog.result.then(
      (result) => {
        if (result === 'updated') {
          this.refreshList();
        }
      },
      () => {}
    );
  }

  openChildrenInfoModal(user: User): void {
    this.currentDialog = this.modalService.open(UserChildrenInfoComponent, {
      scrollable: true,
      windowClass: 'modal-children-info',
      size: 'lg',
      centered: true,
    });

    this.currentDialog.componentInstance.user = user;

    this.currentDialog.result.then(
      (result) => {
        if (result === 'updated') {
          this.refreshList();
        }
      },
      () => {}
    );
  }

  openDeleteConfirmModal(user: User): void {
    this.currentDialog = this.modalService.open(ConfirmDialogComponent, {
      scrollable: true,
      windowClass: 'modal-confirm',
      size: 'md',
      centered: true,
    });

    this.currentDialog.componentInstance.message =
      'Are you sure that you want to delete user ' +
      user.firstName +
      ' ' +
      user.lastName +
      '?';

    this.currentDialog.result.then(
      (result) => {
        if (result.status === 'confirmed') {
          this.userService.deleteUser(user.id).subscribe({
            next: () => this.refreshList(),
            error: (error) => (this.errorMessage = error.error.error),
          });
        }
      },
      (error) => {
        console.log('Dialog close error: ' + error);
      }
    );
  }

  generateReport(): void {
    this.httpClient
      .get(`${environment.backendApiUrl}/v1/admin/users/report`, {
        observe: 'response',
        responseType: 'blob' as 'json',
      })
      .subscribe((response: HttpResponse<Blob>) => {
        const filename = response.headers
          .get('content-disposition')
          .split(';')[1]
          .split('filename')[1]
          .split('=')[1]
          .trim();
        filename.replace('"', ''); // to remove the first " from filename
        filename.replace('"', ''); // to remove the last " from filename
        saveAs(response.body, filename);
      });
  }

  handlePageChange(event): void {
    this.pageNumber = event;
    this.refreshList();
  }

  handleReportPageChange(event: number): void {
    this.reportPageNumber = event;
    this.refreshHistory();
  }

  handlePageSizeChange(event): void {
    this.pageSize = event.target.value;
    this.pageNumber = 1;
    this.refreshList();
  }

  handleReportPageSizeChange(event): void {
    this.reportPageSize = event.target.value;
    this.reportPageNumber = 1;
    this.refreshHistory();
  }

  getMinLimit(): number {
    return this.pageSize * (this.pageNumber - 1) + 1;
  }

  getMaxLimit(): number {
    const maxLimit = this.pageSize * this.pageNumber;
    return maxLimit <= this.totalElements ? maxLimit : this.totalElements;
  }

  getReportMinLimit(): number {
    return this.reportPageSize * (this.reportPageNumber - 1) + 1;
  }

  getReportMaxLimit(): number {
    const maxLimit = this.reportPageSize * this.reportPageNumber;
    return maxLimit <= this.reportTotalElements ? maxLimit : this.reportTotalElements;
  }

  openAddUserSubscriptionModal(user: User): void {
    this.currentDialog = this.modalService.open(UserAddSubscriptionComponent, {
      scrollable: false,
      windowClass: 'modal-children-info',
      size: 'md',
      centered: true,
    });

    this.currentDialog.componentInstance.user = user;
    this.currentDialog.componentInstance.subscriptions = this.subscriptions;

    this.currentDialog.result.then((response: UserAddSubscriptionModalResult | undefined) => {
      if (!response) {
        return;
      }
      if (response.paymentMethod === PaymentMethods.LINK) {
        this.subscriptionService.createPaymentLink({
          userId: response.user.id,
          amount: response.amount,
          taxRequired: response.taxRequired,
          childId: response.childId
        }).subscribe(() => {
          this.refreshList();
        });
      } else {
        this.subscriptionService.addPaidByCashSubscription({
          subscriptionId: response.subscription.id,
          userId: response.user.id,
          startDate: response.dateStart,
          paymentMethod: response.paymentMethod,
          childId: response.childId,
          bundleIds: response.bundleIds
        }).subscribe(() => {
          this.refreshList();
        });
      }
    });
  }

  changeCustomersViewMode(): void {
    this.viewMode = Number(!this.viewMode);
    if (this.viewMode === CustomerViewModes.REPORT) {
      this.refreshHistory();
    } else {
      this.reportPageNumber = 1;
      this.reportPageSize = this.pageSizes[0];
      this.reportTotalElements = 0;
      delete this.filters.userId;
      delete this.filters.paymentMethod;
      delete this.filters.createdDateFrom;
      delete this.filters.createdDateTo;
      delete this.filters.subscriptionName;
      this.fromDate = null;
      this.toDate = null;
    }
  }

  handleUserFilter(event: User): void {
    if (event) {
      this.filters.userId = event.id;
    } else {
      delete this.filters.userId;
    }
    this.refreshHistory();
  }

  handlePaymentMethodFilter(event): void {
    if (event) {
      this.filters.paymentMethod = event;
    } else {
      delete this.filters.paymentMethod;
    }
    this.refreshHistory();
  }

  handleDateSelectFilter(date: NgbDate): void {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date && date.equals(this.fromDate)) {
      this.fromDate = null;
    } else if (this.fromDate && !this.toDate && date && date.after(this.fromDate)) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  close(): void {
    this.datepicker.close();
    this.fromDate = null;
    this.toDate = null;
    delete this.filters.createdDateFrom;
    delete this.filters.createdDateTo;
    this.refreshHistory();
  }

  handleSubscriptionFilter(subscription: Subscription): void {
    if (subscription) {
      this.filters.subscriptionName = subscription.name;
    } else {
      delete this.filters.subscriptionName;
    }
    this.refreshHistory();
  }

  onSort({ column, direction }: SortEvent): void {
    this.headers.forEach((header) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    this.column = column;
    this.direction = direction;
    this.refreshHistory();
  }

  getFromToDate(fromDate, toDate): string {
    if (fromDate === null || toDate === null) {
      return '';
    }
    return this.formatter.format(fromDate) + ' - ' + this.formatter.format(toDate);
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
  }

  checkRangeAndGetBookedLessons(): void {
    this.datepicker.close();
    const dateFrom = new Date(Date.UTC(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day, 0, 0, 0, 0));
    this.filters.createdDateFrom = dateFrom.toISOString();
    const dateTo = new Date(Date.UTC(this.toDate.year, this.toDate.month - 1, this.toDate.day, 23, 59, 59, 999));
    this.filters.createdDateTo = dateTo.toISOString();
    this.refreshHistory();
  }

  isHovered(date: NgbDate): boolean {
    return (
      this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate): boolean {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate): boolean {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  checkAllergies(children: Child[]): boolean {
    return children.map(ch => ch.allergy).filter(allergy => allergy !== null).length > 0;
  }
}
